<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TemplatePreview from 'dashboard/components-next/template-preview/TemplatePreview.vue';
import { PLATFORMS } from 'dashboard/services/TemplateConstants';
import {
  COMPONENT_TYPES,
  MEDIA_FORMATS,
  findComponentByType,
} from 'dashboard/helper/templateHelper';
import {
  previewVariables as buildPreviewVariables,
  templateWithHeaderPreview,
  templateWithMediaPreview,
  variableDescriptors,
} from 'custom/helpers/channelTemplatePresets';
import WhatsappCampaignsAPI from 'custom/api/whatsappCampaigns';

const emit = defineEmits(['created', 'cancel']);
const { t } = useI18n();

const formState = {
  inboxes: useMapGetter('inboxes/getWhatsAppInboxes'),
  getFilteredWhatsAppTemplates: useMapGetter(
    'inboxes/getFilteredWhatsAppTemplates'
  ),
};

const state = reactive({
  title: '',
  inboxId: null,
  templateId: null,
  scheduledAt: null,
  file: null,
  mappings: {},
});

const csvColumns = ref([]);
const preview = ref(null);
const isPreviewing = ref(false);
const isConfirming = ref(false);
const formError = ref('');
const fileInput = ref(null);

const currentDateTime = computed(() => {
  const now = new Date();
  const localTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return localTime.toISOString().slice(0, 16);
});

const mapToOptions = (items, valueKey, labelKey) =>
  items?.map(item => ({
    value: item[valueKey],
    label: item[labelKey],
  })) ?? [];

const inboxOptions = computed(() =>
  mapToOptions(formState.inboxes.value, 'id', 'name')
);

const templateOptions = computed(() => {
  if (!state.inboxId) return [];

  return formState.getFilteredWhatsAppTemplates
    .value(state.inboxId)
    .map(template => ({
      value: template.id,
      label: `${template.name.replace(/_/g, ' ')} (${template.language || 'en'})`,
      template,
    }));
});

const selectedTemplate = computed(
  () =>
    templateOptions.value.find(option => option.value === state.templateId)
      ?.template
);

const bodyText = computed(() => {
  const body = findComponentByType(
    selectedTemplate.value,
    COMPONENT_TYPES.BODY
  );
  return body?.text || '';
});

const hasMediaHeader = computed(() => {
  const header = findComponentByType(
    selectedTemplate.value,
    COMPONENT_TYPES.HEADER
  );
  return MEDIA_FORMATS.includes(header?.format);
});

const parameterDescriptors = computed(() => {
  if (!selectedTemplate.value) return [];

  return variableDescriptors(selectedTemplate.value);
});

const isReadyToPreview = computed(() =>
  Boolean(
    state.title.trim() &&
      state.inboxId &&
      state.templateId &&
      state.scheduledAt &&
      state.file
  )
);

const isReadyToConfirm = computed(
  () => preview.value?.import?.status === 'validated'
);

const actualPreviewRow = computed(() => {
  const rows = preview.value?.rows || [];
  return (
    rows.find(row => {
      const rowPreview = row.template_preview;
      return (
        rowPreview && (!hasMediaHeader.value || Boolean(rowPreview.media_url))
      );
    }) || null
  );
});
const actualTemplatePreview = computed(
  () => actualPreviewRow.value?.template_preview || null
);
const previewMediaUrl = computed(
  () => actualTemplatePreview.value?.media_url || ''
);
const canRenderActualPreview = computed(
  () =>
    Boolean(actualTemplatePreview.value) &&
    (!hasMediaHeader.value || Boolean(previewMediaUrl.value))
);
const previewTemplate = computed(() => {
  if (!canRenderActualPreview.value || !selectedTemplate.value) return null;

  const withMedia = templateWithMediaPreview(
    selectedTemplate.value,
    previewMediaUrl.value
  );
  return templateWithHeaderPreview(
    withMedia,
    actualTemplatePreview.value.variables?.header || {}
  );
});
const previewVariables = computed(() =>
  buildPreviewVariables(actualTemplatePreview.value?.variables || {})
);

const selectedTemplateParams = computed(() => ({
  name: selectedTemplate.value?.name || '',
  namespace: selectedTemplate.value?.namespace || '',
  category: selectedTemplate.value?.category || 'UTILITY',
  language: selectedTemplate.value?.language || 'en_US',
  processed_params: {},
}));

const clearPreview = () => {
  preview.value = null;
  formError.value = '';
};

const canonicalHeader = value =>
  value
    .replace(/^\uFEFF/, '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');

const parseCsvLine = line => {
  const values = [];
  let value = '';
  let quoted = false;

  for (let index = 0; index < line.length; index += 1) {
    const character = line[index];
    const nextCharacter = line[index + 1];
    if (character === '"' && quoted && nextCharacter === '"') {
      value += '"';
      index += 1;
    } else if (character === '"') {
      quoted = !quoted;
    } else if (character === ',' && !quoted) {
      values.push(value);
      value = '';
    } else if (character === ';' && !quoted) {
      values.push(value);
      value = '';
    } else {
      value += character;
    }
  }
  values.push(value);
  return values.map(canonicalHeader);
};

const readCsvColumns = file =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = event => {
      const firstLine = String(event.target.result || '')
        .split(/\r?\n/)
        .find(line => line.trim());
      if (!firstLine) {
        reject(new Error(t('CAMPAIGN.WHATSAPP.MASS.INVALID_CSV')));
        return;
      }
      resolve(parseCsvLine(firstLine));
    };
    reader.onerror = () =>
      reject(new Error(t('CAMPAIGN.WHATSAPP.MASS.INVALID_CSV')));
    reader.readAsText(file, 'UTF-8');
  });

const handleFileChange = async event => {
  state.file = event.target.files?.[0] || null;
  state.mappings = {};
  csvColumns.value = [];
  clearPreview();
  if (!state.file) return;

  try {
    csvColumns.value = await readCsvColumns(state.file);
  } catch (error) {
    formError.value = error.message;
  }
};

const setMapping = (path, column) => {
  state.mappings[path] = column ? { source: 'csv', column } : undefined;
  clearPreview();
};

const mappingColumn = path => state.mappings[path]?.column || '';

const missingMappings = computed(() =>
  parameterDescriptors.value.filter(
    descriptor => !mappingColumn(descriptor.path)
  )
);

const formatToUTCString = localDateTime =>
  localDateTime ? new Date(localDateTime).toISOString() : null;

const errorMessage = error =>
  error?.response?.data?.error ||
  error?.response?.message ||
  t('CAMPAIGN.WHATSAPP.MASS.API.ERROR_MESSAGE');

const validateForm = () => {
  if (!isReadyToPreview.value) {
    formError.value = t('CAMPAIGN.WHATSAPP.MASS.FORM_REQUIRED');
    return false;
  }
  if (missingMappings.value.length) {
    formError.value = t('CAMPAIGN.WHATSAPP.MASS.MAPPING_REQUIRED');
    return false;
  }
  if (!csvColumns.value.includes('phone_number')) {
    formError.value = t('CAMPAIGN.WHATSAPP.MASS.PHONE_COLUMN_REQUIRED');
    return false;
  }
  return true;
};

const validateCsv = async () => {
  if (!validateForm()) return;

  isPreviewing.value = true;
  formError.value = '';
  const payload = new FormData();
  payload.append('file', state.file);
  payload.append('inbox_id', state.inboxId);
  payload.append(
    'template_params',
    JSON.stringify(selectedTemplateParams.value)
  );
  payload.append('parameter_mapping', JSON.stringify(state.mappings));

  try {
    const response = await WhatsappCampaignsAPI.preview(payload);
    preview.value = response.data;
  } catch (error) {
    formError.value = errorMessage(error);
  } finally {
    isPreviewing.value = false;
  }
};

const confirmImport = async () => {
  if (!isReadyToConfirm.value) return;

  isConfirming.value = true;
  formError.value = '';
  try {
    const response = await WhatsappCampaignsAPI.confirm({
      import_id: preview.value.import.id,
      title: state.title,
      scheduled_at: formatToUTCString(state.scheduledAt),
    });
    emit('created', response.data);
  } catch (error) {
    formError.value = errorMessage(error);
  } finally {
    isConfirming.value = false;
  }
};

const handleCancel = () => emit('cancel');

watch(
  () => state.inboxId,
  () => {
    state.templateId = null;
    state.mappings = {};
    csvColumns.value = [];
    clearPreview();
  }
);

watch(
  () => state.templateId,
  () => {
    state.mappings = {};
    clearPreview();
  }
);
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="confirmImport">
    <Input
      v-model="state.title"
      :label="t('CAMPAIGN.WHATSAPP.MASS.TITLE.LABEL')"
      :placeholder="t('CAMPAIGN.WHATSAPP.MASS.TITLE.PLACEHOLDER')"
    />

    <div class="flex flex-col gap-1">
      <label
        for="mass-inbox"
        class="mb-0.5 text-sm font-medium text-n-slate-12"
      >
        {{ t('CAMPAIGN.WHATSAPP.MASS.INBOX.LABEL') }}
      </label>
      <ComboBox
        id="mass-inbox"
        v-model="state.inboxId"
        :options="inboxOptions"
        :placeholder="t('CAMPAIGN.WHATSAPP.MASS.INBOX.PLACEHOLDER')"
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
      />
    </div>

    <div class="flex flex-col gap-1">
      <label
        for="mass-template"
        class="mb-0.5 text-sm font-medium text-n-slate-12"
      >
        {{ t('CAMPAIGN.WHATSAPP.MASS.TEMPLATE.LABEL') }}
      </label>
      <ComboBox
        id="mass-template"
        v-model="state.templateId"
        :options="templateOptions"
        :placeholder="t('CAMPAIGN.WHATSAPP.MASS.TEMPLATE.PLACEHOLDER')"
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
      />
    </div>

    <div
      v-if="selectedTemplate"
      class="flex flex-col gap-2 p-3 rounded-lg bg-n-alpha-black2"
    >
      <div class="flex flex-wrap items-center justify-between gap-2">
        <span class="text-xs font-medium text-n-slate-11">
          {{ t('CAMPAIGN.WHATSAPP.MASS.TEMPLATE.PREVIEW') }}
        </span>
        <span
          v-if="canRenderActualPreview"
          class="text-xs font-medium text-n-teal-11"
        >
          {{ t('CAMPAIGN.WHATSAPP.MASS.TEMPLATE.ACTUAL_PREVIEW') }}
        </span>
      </div>
      <div v-if="canRenderActualPreview" class="flex flex-col gap-3">
        <div class="flex justify-center">
          <TemplatePreview
            :template="previewTemplate"
            :variables="previewVariables"
            :platform="PLATFORMS.WHATSAPP"
          />
        </div>
        <p v-if="hasMediaHeader" class="text-xs text-center text-n-slate-11">
          {{
            t('CAMPAIGN.WHATSAPP.MASS.TEMPLATE.MEDIA_SOURCE_CHANNEL', {
              row: actualPreviewRow.row_number,
            })
          }}
        </p>
      </div>
      <span v-else class="text-sm whitespace-pre-wrap text-n-slate-12">{{
        bodyText
      }}</span>
      <p
        v-if="preview && hasMediaHeader && !previewMediaUrl"
        class="text-xs text-n-ruby-9"
      >
        {{ t('CAMPAIGN.WHATSAPP.MASS.TEMPLATE.MEDIA_PREVIEW_MISSING') }}
      </p>
      <p v-else-if="!preview && hasMediaHeader" class="text-xs text-n-slate-11">
        {{ t('CAMPAIGN.WHATSAPP.MASS.TEMPLATE.MEDIA_PREVIEW_PENDING') }}
      </p>
    </div>

    <div class="flex flex-col gap-2">
      <label for="mass-csv" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.MASS.FILE.LABEL') }}
      </label>
      <input
        id="mass-csv"
        ref="fileInput"
        type="file"
        accept=".csv,text/csv"
        class="block w-full text-sm text-n-slate-11 file:me-3 file:rounded-md file:border-0 file:bg-n-blue-9 file:px-3 file:py-2 file:text-sm file:font-medium file:text-white hover:file:bg-n-blue-10"
        @change="handleFileChange"
      />
      <p class="text-xs text-n-slate-11">
        {{ t('CAMPAIGN.WHATSAPP.MASS.FILE.INFO') }}
      </p>
      <p v-if="csvColumns.length" class="text-xs text-n-slate-11">
        {{
          t('CAMPAIGN.WHATSAPP.MASS.FILE.COLUMNS', {
            columns: csvColumns.join(', '),
          })
        }}
      </p>
    </div>

    <div v-if="parameterDescriptors.length" class="flex flex-col gap-2">
      <div>
        <p class="text-sm font-medium text-n-slate-12">
          {{ t('CAMPAIGN.WHATSAPP.MASS.MAPPING.TITLE') }}
        </p>
        <p class="text-xs text-n-slate-11">
          {{ t('CAMPAIGN.WHATSAPP.MASS.MAPPING.INFO') }}
        </p>
      </div>
      <div
        v-for="descriptor in parameterDescriptors"
        :key="descriptor.path"
        class="flex items-center gap-3"
      >
        <span class="w-1/2 text-xs text-n-slate-11">{{ descriptor.path }}</span>
        <select
          :value="mappingColumn(descriptor.path)"
          class="w-1/2 min-w-0 px-3 py-2 text-sm border rounded-md outline-none bg-n-alpha-black2 border-n-weak text-n-slate-12 focus:border-n-blue-9"
          @change="setMapping(descriptor.path, $event.target.value)"
        >
          <option value="">
            {{ t('CAMPAIGN.WHATSAPP.MASS.MAPPING.PLACEHOLDER') }}
          </option>
          <option v-for="column in csvColumns" :key="column" :value="column">
            {{ column }}
          </option>
        </select>
      </div>
    </div>

    <Input
      v-model="state.scheduledAt"
      :label="t('CAMPAIGN.WHATSAPP.MASS.SCHEDULED_AT.LABEL')"
      type="datetime-local"
      :min="currentDateTime"
    />

    <div
      v-if="preview"
      class="flex flex-col gap-3 p-3 border rounded-lg border-n-weak bg-n-alpha-black2"
    >
      <div class="flex flex-wrap gap-3 text-xs text-n-slate-11">
        <span>{{
          t('CAMPAIGN.WHATSAPP.MASS.SUMMARY.TOTAL', {
            count: preview.import.total_rows,
          })
        }}</span>
        <span>{{
          t('CAMPAIGN.WHATSAPP.MASS.SUMMARY.VALID', {
            count: preview.import.valid_rows,
          })
        }}</span>
        <span>{{
          t('CAMPAIGN.WHATSAPP.MASS.SUMMARY.INVALID', {
            count: preview.import.invalid_rows,
          })
        }}</span>
      </div>
      <p v-if="!isReadyToConfirm" class="text-xs text-n-ruby-9">
        {{ t('CAMPAIGN.WHATSAPP.MASS.PREVIEW_HAS_ERRORS') }}
      </p>
      <div class="overflow-x-auto border rounded-md border-n-weak">
        <table class="w-full text-xs text-start">
          <thead class="text-n-slate-11 bg-n-alpha-2">
            <tr>
              <th class="px-3 py-2 font-medium">
                {{ t('CAMPAIGN.WHATSAPP.MASS.TABLE.ROW') }}
              </th>
              <th class="px-3 py-2 font-medium">
                {{ t('CAMPAIGN.WHATSAPP.MASS.TABLE.PHONE') }}
              </th>
              <th class="px-3 py-2 font-medium">
                {{ t('CAMPAIGN.WHATSAPP.MASS.TABLE.CONTACT') }}
              </th>
              <th class="px-3 py-2 font-medium">
                {{ t('CAMPAIGN.WHATSAPP.MASS.TABLE.TEMPLATE') }}
              </th>
              <th class="px-3 py-2 font-medium">
                {{ t('CAMPAIGN.WHATSAPP.MASS.TABLE.STATUS') }}
              </th>
              <th class="px-3 py-2 font-medium">
                {{ t('CAMPAIGN.WHATSAPP.MASS.TABLE.ERROR') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in preview.rows"
              :key="row.id"
              class="border-t border-n-weak"
            >
              <td class="px-3 py-2 tabular-nums text-n-slate-11">
                {{ row.row_number }}
              </td>
              <td class="px-3 py-2 tabular-nums text-n-slate-11">
                {{ row.phone_number || '-' }}
              </td>
              <td class="px-3 py-2 text-n-slate-12">
                {{
                  row.contact?.name ||
                  t('CAMPAIGN.WHATSAPP.MASS.TABLE.NEW_RECIPIENT')
                }}
              </td>
              <td class="max-w-48 px-3 py-2 truncate text-n-slate-12">
                {{ preview.import.template_name || '-' }}
              </td>
              <td class="px-3 py-2 text-n-slate-11">{{ row.status }}</td>
              <td class="px-3 py-2 text-n-ruby-9">
                {{ row.errors?.map(error => error.message).join('; ') || '-' }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <p
      v-if="formError"
      class="p-2 text-sm rounded-md bg-n-ruby-9/20 text-n-ruby-9"
    >
      {{ formError }}
    </p>

    <div class="flex items-center justify-between w-full gap-3">
      <Button
        variant="faded"
        color="slate"
        type="button"
        :label="t('CAMPAIGN.WHATSAPP.MASS.BUTTONS.CANCEL')"
        class="w-full bg-n-alpha-2 text-n-blue-11 hover:bg-n-alpha-3"
        @click="handleCancel"
      />
      <Button
        v-if="!preview"
        :label="t('CAMPAIGN.WHATSAPP.MASS.BUTTONS.PREVIEW')"
        type="button"
        :is-loading="isPreviewing"
        :disabled="isPreviewing || !isReadyToPreview"
        class="w-full"
        @click="validateCsv"
      />
      <Button
        v-else
        :label="t('CAMPAIGN.WHATSAPP.MASS.BUTTONS.CONFIRM')"
        type="submit"
        :is-loading="isConfirming"
        :disabled="isConfirming || !isReadyToConfirm"
        class="w-full"
      />
    </div>
  </form>
</template>
